import { marked } from 'marked';

/** Markdown → HTML for tiptap initial content. */
export function markdownToHtml(md: string): string {
  if (!md) return '';
  return marked.parse(md, { async: false, gfm: true, breaks: false }) as string;
}

/** HTML → Markdown serializer for the limited set our toolbar produces. */
export function htmlToMarkdown(html: string): string {
  if (!html) return '';
  if (typeof window === 'undefined') return html;

  const wrap = document.createElement('div');
  wrap.innerHTML = html;

  const inline = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return (node.textContent || '').replace(/\n+/g, ' ');
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node as HTMLElement;
    const inner = Array.from(el.childNodes).map(inline).join('');
    switch (el.tagName) {
      case 'STRONG':
      case 'B':
        return inner ? `**${inner}**` : '';
      case 'EM':
      case 'I':
        return inner ? `*${inner}*` : '';
      case 'CODE':
        return inner ? `\`${inner}\`` : '';
      case 'A': {
        const href = el.getAttribute('href') || '';
        return inner ? `[${inner}](${href})` : '';
      }
      case 'BR':
        return '\n';
      case 'IMG': {
        const src = el.getAttribute('src') || '';
        const alt = el.getAttribute('alt') || '';
        return src ? `![${alt}](${src})` : '';
      }
      default:
        return inner;
    }
  };

  const block = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = (node.textContent || '').trim();
      return t ? t + '\n\n' : '';
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const el = node as HTMLElement;
    switch (el.tagName) {
      case 'H1':
        return `# ${inline(el).trim()}\n\n`;
      case 'H2':
        return `## ${inline(el).trim()}\n\n`;
      case 'H3':
        return `### ${inline(el).trim()}\n\n`;
      case 'H4':
        return `#### ${inline(el).trim()}\n\n`;
      case 'P':
        return `${inline(el).trim()}\n\n`;
      case 'UL':
        return Array.from(el.children)
          .map((li) => `- ${inline(li).trim()}`)
          .join('\n') + '\n\n';
      case 'OL':
        return Array.from(el.children)
          .map((li, i) => `${i + 1}. ${inline(li).trim()}`)
          .join('\n') + '\n\n';
      case 'BLOCKQUOTE':
        return `> ${inline(el).trim()}\n\n`;
      case 'HR':
        return `---\n\n`;
      case 'IMG': {
        const src = el.getAttribute('src') || '';
        const alt = el.getAttribute('alt') || '';
        return src ? `![${alt}](${src})\n\n` : '';
      }
      default:
        return Array.from(el.childNodes).map(block).join('');
    }
  };

  return Array.from(wrap.childNodes).map(block).join('').trim();
}

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}
